import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { DEFAULT_STATS, STATS_CACHE_DURATION, STORAGE_KEYS } from '../pages/bankfeed/bankFeedUtils';

const PAGE_SIZE = 200;

/**
 * useBankFeedTransactions
 * --------------------------------------------------------------------------
 * Phase 1 part 2 - extracts the *list-shaped* bank feed data fetchers out
 * of BankFeedCenterPage.jsx.
 *
 * Inputs (filters/pagination owned by the parent because they are also
 * UI state - search box, pagination buttons, GL filter dropdown, etc.):
 *   - pendingPage, processedPage          : 1-based pagination
 *   - filterGlCode                        : 'all' or a GL code
 *   - filterStartDate, filterEndDate      : 'YYYY-MM-DD' or ''
 *   - fiscalPeriod                        : { start, end } - drives stats only
 *
 * Outputs:
 *   - stats / pendingItems / processedItems / duplicateItems / failedItems
 *     / potentialDuplicates / uploadedStatements
 *   - totals for pending and processed (count + total_pages)
 *   - setters (used by row-level optimistic ops like delete-from-list)
 *   - fetch* refetchers (each can be triggered after a mutation)
 *
 * Stats is initialized from sessionStorage cache (TTL = STATS_CACHE_DURATION)
 * to make back-navigation feel instant; the next fetch refreshes it.
 *
 * All fetchers are mounted-ref guarded.
 */
export function useBankFeedTransactions({
  pendingPage,
  processedPage,
  filterGlCode,
  filterStartDate,
  filterEndDate,
  fiscalPeriod,
}) {
  const isMountedRef = useRef(true);

  const [stats, setStats] = useState(() => {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEYS.STATS_CACHE);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < STATS_CACHE_DURATION) return data;
      }
    } catch {
      // Ignore cache errors
    }
    return DEFAULT_STATS;
  });

  const [pendingItems, setPendingItems] = useState([]);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [pendingTotalCount, setPendingTotalCount] = useState(0);

  const [processedItems, setProcessedItems] = useState([]);
  const [processedTotalPages, setProcessedTotalPages] = useState(1);
  const [processedTotalCount, setProcessedTotalCount] = useState(0);

  const [duplicateItems, setDuplicateItems] = useState([]);
  const [failedItems, setFailedItems] = useState([]);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [uploadedStatements, setUploadedStatements] = useState([]);

  // iter1107 - bank-pending rows (visible-but-inert bucket)
  const [awaitingBankItems, setAwaitingBankItems] = useState([]);
  const [awaitingBankTotalCount, setAwaitingBankTotalCount] = useState(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const fetchStats = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const params = new URLSearchParams();
      if (fiscalPeriod?.start) params.append('start_date', fiscalPeriod.start);
      if (fiscalPeriod?.end) params.append('end_date', fiscalPeriod.end);
      const res = await axios.get(`${API}/bank-feeds/stats?${params.toString()}`);
      if (!isMountedRef.current) return;
      setStats(res.data);
      try {
        sessionStorage.setItem(STORAGE_KEYS.STATS_CACHE, JSON.stringify({
          data: res.data,
          timestamp: Date.now(),
        }));
      } catch {
        // Ignore cache errors
      }
    } catch {
      // Silent
    }
  }, [fiscalPeriod]);

  // Background retro-sweep: poll until it finishes, then toast + refetch.
  const sweepPollRef = useRef({ timer: null, lastSeen: null });
  const refetchProcessedRef = useRef(null);
  const watchSweep = useCallback((onDone) => {
    const st = sweepPollRef.current;
    if (st.timer) return;
    let ticks = 0;
    const tick = async () => {
      ticks += 1;
      try {
        const res = await axios.get(`${API}/bank-feeds/sweep-status`);
        const d = res.data || {};
        if (!isMountedRef.current) return;
        if (!d.running) {
          st.timer = null;
          if (d.completed_at && d.completed_at !== st.lastSeen) {
            st.lastSeen = d.completed_at;
            if (d.rows_posted > 0) {
              toast.success(`${d.rows_posted} row${d.rows_posted === 1 ? '' : 's'} just posted automatically`, {
                description: 'Vendors you had confirmed 3 times were booked without waiting for you.',
              });
            }
            if (d.rows_posted > 0 || d.rows_updated > 0) onDone?.();
          }
          return;
        }
      } catch {
        // keep polling
      }
      if (ticks < 45 && isMountedRef.current) st.timer = setTimeout(tick, 2000);
      else st.timer = null;
    };
    st.timer = setTimeout(tick, 1500);
  }, []);

  const fetchPendingItems = useCallback(async (page = pendingPage) => {
    if (!isMountedRef.current) return;
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, page });
      if (filterGlCode !== 'all') params.set('gl_code', filterGlCode);
      if (filterStartDate) params.set('start_date', filterStartDate);
      if (filterEndDate) params.set('end_date', filterEndDate);
      const res = await axios.get(`${API}/bank-feeds/pending-review?${params}`);
      if (!isMountedRef.current) return;
      setPendingItems(res.data.items || []);
      setPendingTotalPages(res.data.total_pages || 1);
      setPendingTotalCount(res.data.count || 0);
      if (res.data.sweep_running) {
        watchSweep(() => {
          fetchPendingItems(page);
          fetchStats();
          refetchProcessedRef.current?.();
        });
      }
    } catch {
      // Silent
    }
  }, [pendingPage, filterGlCode, filterStartDate, filterEndDate, watchSweep, fetchStats]);

  const fetchProcessedItems = useCallback(async (page = processedPage) => {
    if (!isMountedRef.current) return;
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, page });
      if (filterGlCode !== 'all') params.set('gl_code', filterGlCode);
      if (filterStartDate) params.set('start_date', filterStartDate);
      if (filterEndDate) params.set('end_date', filterEndDate);
      const res = await axios.get(`${API}/bank-feeds/history?${params}`);
      if (!isMountedRef.current) return;
      setProcessedItems(res.data.items || []);
      setProcessedTotalPages(res.data.total_pages || 1);
      setProcessedTotalCount(res.data.count || 0);
    } catch {
      // Silent
    }
  }, [processedPage, filterGlCode, filterStartDate, filterEndDate]);
  refetchProcessedRef.current = fetchProcessedItems;

  const fetchDuplicateItems = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-feeds/duplicates?limit=50`);
      if (isMountedRef.current) setDuplicateItems(res.data.items || []);
    } catch {
      // Silent
    }
  }, []);

  const fetchFailedItems = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-feeds/failed?limit=50`);
      if (isMountedRef.current) setFailedItems(res.data.items || []);
    } catch {
      // Silent
    }
  }, []);

  const fetchPotentialDuplicates = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-feeds/potential-duplicates?limit=50`);
      if (isMountedRef.current) setPotentialDuplicates(res.data.items || []);
    } catch {
      // Silent
    }
  }, []);

  // iter1107 - bank-pending list. Small page size; usually few rows.
  const fetchAwaitingBankItems = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const params = new URLSearchParams({ limit: 100, page: 1 });
      if (filterStartDate) params.set('start_date', filterStartDate);
      if (filterEndDate) params.set('end_date', filterEndDate);
      const res = await axios.get(`${API}/bank-feeds/awaiting-bank?${params}`);
      if (!isMountedRef.current) return;
      setAwaitingBankItems(res.data.items || []);
      setAwaitingBankTotalCount(res.data.count || 0);
    } catch {
      // Silent
    }
  }, [filterStartDate, filterEndDate]);

  const fetchUploadedStatements = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-feeds/uploaded-statements?limit=200&status_filter=completed_and_blocked`);
      if (isMountedRef.current) setUploadedStatements(res.data.statements || []);
    } catch {
      // Silent
    }
  }, []);

  return {
    // values
    stats, setStats,
    pendingItems, setPendingItems, pendingTotalPages, pendingTotalCount,
    setPendingTotalPages, setPendingTotalCount,
    processedItems, setProcessedItems, processedTotalPages, processedTotalCount,
    setProcessedTotalPages, setProcessedTotalCount,
    duplicateItems, setDuplicateItems,
    failedItems, setFailedItems,
    potentialDuplicates, setPotentialDuplicates,
    uploadedStatements, setUploadedStatements,
    // iter1107 - awaiting-bank bucket
    awaitingBankItems, setAwaitingBankItems, awaitingBankTotalCount,
    // refetchers
    fetchStats,
    fetchPendingItems,
    fetchProcessedItems,
    fetchDuplicateItems,
    fetchFailedItems,
    fetchPotentialDuplicates,
    fetchUploadedStatements,
    fetchAwaitingBankItems,
  };
}

export default useBankFeedTransactions;
